import { map as Rmap } from "ramda";
import api from "@/api";
import { JSConn } from "@/utilities/js-conn";

export interface Client {
  id: string;
  name: string;
  status: string;
  manager: string;
  total_acres?: number;
  count_of_fields?: number;
  count_of_work_orders?: number;
  phone_numbers: PhoneNumber[];
  client_fields?: ClientField[];
  work_orders?: WorkOrder[];
}

export interface ClientForTable extends Client {
  phoneNumber: string;
}

export interface PhoneNumber {
  id: string;
  number: string;
  classification: string;
  sort_order: number;
}

interface ClientField {
  id: string;
  name: string;
  description: string;
  acres: string;
  commodity_id: string;
  commodity: string;
}

interface WorkOrder {
  id: string;
  name: string;
  description: string;
  status: string;
}

interface ClientState {
  clients: Client[];
  client: Client | {};
}

const emptyClient: Client = {
  id: "",
  name: "",
  manager: "",
  status: "active",
  phone_numbers: [
    {
      id: "",
      number: "",
      classification: "cell",
      sort_order: 100,
    },
  ],
};

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    client: {
      ...jsConn.formParams,
    },
  };
};

const itemDetails = {
  pluralName: "clients",
  singularName: "client",
};

const state: ClientState = {
  clients: [],
  client: {},
};

const mutations = {
  LOAD_CLIENTS(state: ClientState, payload: Client[]) {
    state.clients = payload;
  },
  LOAD_CLIENT(state: ClientState, payload: Client) {
    state.client = payload;
  },
};

const actions = {
  needClients(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_CLIENTS", jsConn.response);
      return Promise.resolve(jsConn);
    });
  },
  needClient(context: any, jsConn: JSConn) {
    return api.getItem(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_CLIENT", jsConn.response);
      return Promise.resolve(jsConn);
    });
  },
  createClient(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_CLIENT", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  updateClient(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .updateItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_CLIENT", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  deleteClient(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);

    return api
      .deleteItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_CLIENT", emptyClient);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  clients: (state: ClientState) => {
    return state.clients;
  },
  clientsForTable: (state: ClientState) => {
    // this takes the first phone number and adds it as a field to the base item
    return Rmap(item => {
      let ph;
      if (item.phone_numbers.length > 0) {
        ph = item.phone_numbers[0].number;
      } else {
        ph = "";
      }

      return {
        ...item,
        phoneNumber: ph,
      };
    }, state.clients);
  },

  client: (state: ClientState) => state.client,

  clientsForAutocomplete: (state: ClientState) => {
    // map over the items and just return a label and an id
    return Rmap(client => {
      return {
        value: client.id,
        label: `${client.name} - ${client.manager}`,
      };
    }, state.clients);
  },

  clientsForAutocompleteId: (state: ClientState) => {
    // map over the items and just return a label and an id
    return Rmap(client => {
      return {
        id: client.id,
        label: `${client.name} - ${client.manager}`,
      };
    }, state.clients);
  },
};

const clientsModule = {
  state,
  mutations,
  actions,
  getters,
};

export default clientsModule;
